/**
 * THE WHOO - Sticky Navigation Component
 *
 * 기능: 스크롤 기반 active 섹션 감지, 탭 클릭 시 섹션 이동, 모바일 정렬
 * 스티키 네비게이션 생성 위치 : .c-header 요소를 찾은 뒤 바로 다음에 삽입
 * 핵심요약
 *   - 메뉴 class / data-section / 섹션 class는 반드시 동일해야 함
 *   - .c-header 요소가 있어야 정상 동작함
 *   - 헤더 높이가 다르면 CONFIG.HEADER_HEIGHT 수정 필요
 *   - lazy loading 이미지가 많으면 스크롤 위치 오차 발생 가능
 * 
 * ─────────────────────────────────────────────────────────────────
 * 사용 방법 가이드
 * ─────────────────────────────────────────────────────────────────
 *
 * 1단계: Liquid 파일 하단에 메뉴 설정 추가
 *   {% assign sticky_nav_menu = [
 *     { 'class': 'section-01', 'label': '첫 번째 섹션' },
 *     { 'class': 'section-02', 'label': '두 번째 섹션' },
 *     { 'class': 'section-03', 'label': '세 번째 섹션' }
 *   ] %}
 *   <script>
 *     window.stickyNavMenu = {{ sticky_nav_menu | json }};
 *   </script>
 *
 * 2단계: 각 섹션에 클래스명 추가 (class 값과 정확히 일치해야 함)
 *   <div class="section-01">
 *     <!-- 첫 번째 섹션 내용 -->
 *   </div>
 *   <div class="section-02">
 *     <!-- 두 번째 섹션 내용 -->
 *   </div>
 *   <div class="section-03">
 *     <!-- 세 번째 섹션 내용 -->
 *   </div>
 *
 * 3단계: 스크립트 파일 로드 확인
 *   - cStickyNav.js 파일이 페이지에 로드되어 있어야 함
 *   - .c-header 요소가 페이지에 존재해야 함
 *
 * ─────────────────────────────────────────────────────────────────
 * 사용 클래스
 * ─────────────────────────────────────────────────────────────────
 *
 * 기본 클래스 구조:
 *   .c-sticky-nav                    : 컨테이너
 *   .c-sticky-nav__nav               : 네비게이션 래퍼
 *   .c-sticky-nav__list              : 리스트 컨테이너
 *   .c-sticky-nav__link              : 버튼 (링크)
 *
 * 상태 클래스 (자동 적용):
 *   .c-sticky-nav--active            : 네비게이션 표시 상태, 첫 섹션 근처 도달 시
 *   .c-sticky-nav--header-hidden     : 헤더 숨김 상태 (스크롤 다운)
 *   .c-sticky-nav--header-visible    : 헤더 표시 상태 (스크롤 업)
 *   .c-sticky-nav__link--active      : 현재 활성 섹션 버튼
 *   .c-sticky-nav__list--overflow    : 모바일 오버플로우 상태
 *
 * ─────────────────────────────────────────────────────────────────
 * Liquid + window.stickyNavMenu 동적 생성
 * ─────────────────────────────────────────────────────────────────
 *
 * <script>
 *   window.stickyNavMenu = [
 *     { class: 'pdp-sticky__01', label: 'Offer' },
 *     { class: 'pdp-sticky__02', label: 'Details' },
 *     { class: 'pdp-sticky__03', label: 'Review' }
 *   ];
 * </script>
 *
 * <!-- 타겟 섹션들 -->
 * <div class="pdp-sticky__01">...</div>
 * <div class="pdp-sticky__02">...</div>
 * <div class="pdp-sticky__03">...</div>
 *
 * ─────────────────────────────────────────────────────────────────
 * Liquid에서 동적 메뉴 설정 예시
 * ─────────────────────────────────────────────────────────────────
 *
 * <!-- PDP 페이지 -->
 * {% assign sticky_nav_menu = [{ 'class': 'pdp-sticky__01', 'label': 'Offer' }, { 'class': 'pdp-sticky__02', 'label': 'Details' }, { 'class': 'pdp-sticky__03', 'label': 'Review' }] %}
 * <script> window.stickyNavMenu = {{ sticky_nav_menu | json }}; </script>
 *
 * <!-- Special Offers 페이지 -->
 * {% assign sticky_nav_menu = [{ 'class': 'vip-gift', 'label': 'VIP Event' }, { 'class': 'offer', 'label': 'Offer' }, { 'class': 'loyalty', 'label': 'Loyalty' }] %}
 * <script> window.stickyNavMenu = {{ sticky_nav_menu | json }}; </script>
 * 
 * */
(function() {
    'use strict';

    // 설정값
    const CONFIG = {
        MOBILE_BREAKPOINT: 750,
        HEADER_HEIGHT: { MOBILE: 60, DESKTOP: 80 },
        ACTIVE_THRESHOLD: 30,
        SHOW_NAV_BUFFER: 20
    };

    // 스크롤 방향 추적 변수
    let lastScrollTop = 0;
    // scrollDirection은 UI 클래스 토글용 상태값이다. 정책 판단에는 사용하지 않는다.
    // 정책 판단은 항상 현재 프레임의 scrollTop 기준으로 즉시 계산한다.
    let scrollDirection = 'down';

    /**
     * 스크롤 방향에 따른 정책 기반 헤더 높이 반환
     * 
     * ⚠️ 실제 DOM의 헤더 높이를 측정하지 않습니다.
     * - 스크롤 다운: 헤더가 숨겨진 상태로 가정 → 높이 0
     * - 스크롤 업: 헤더가 표시된 상태로 가정 → 고정 높이 (모바일/데스크톱)
     * 
     * @param {boolean} isDown - 스크롤 다운 여부
     * @returns {number} 정책 기반 헤더 높이 (다운: 0, 업: 모바일/데스크톱 높이)
     */
    const getPolicyHeaderHeight = (isDown) => {
        if (isDown) return 0;
        return window.innerWidth <= CONFIG.MOBILE_BREAKPOINT 
            ? CONFIG.HEADER_HEIGHT.MOBILE 
            : CONFIG.HEADER_HEIGHT.DESKTOP;
    };

    /**
     * Sticky Navigation 초기화
     * 섹션 감지, 스크롤 이벤트, 클릭 이벤트 등록
     * @param {HTMLElement} nav - 네비게이션 컨테이너 요소
     */
    const initStickyNav = (nav) => {
        // 이미 초기화된 네비게이션은 스킵
        if (nav.hasAttribute('data-init')) return;
        nav.setAttribute('data-init', 'true');

        // 네비게이션 버튼과 리스트 요소 선택
        const links = nav.querySelectorAll('.c-sticky-nav__link');  // (버튼) nav > ul > li > button
        const list = nav.querySelector('.c-sticky-nav__list');      // (리스트) nav > ul
        
        // 버튼이 없으면 초기화 중단
        if (links.length === 0) return;
        
        /**
         * 섹션 요소 수집 및 매핑
         * 1. 스크롤 위치에 따라 활성 섹션 감지
         * 2. 네비게이션 표시/숨김 기준점 계산
         * 3. 클릭 시 해당 섹션으로 스크롤 이동
         * 
         * - sections[0].element.offsetTop: 첫 번째 섹션 위치로 네비게이션 표시 기준 계산
         * - sections.forEach(): 각 섹션의 위치를 계산하여 현재 스크롤 위치에 해당하는 활성 섹션 찾기 
         * - section.element: DOM 요소로 offsetTop, offsetHeight 등 위치 정보 접근
         * - section.sectionName: 활성 섹션의 클래스명으로 해당 버튼에 active 클래스 적용
         */
        const getSections = () => {
            const sections = [];
            // 모든 버튼을 순회
            for (const link of links) {
                // 링크의 data-section 속성에서 클래스명 가져오기
                const className = link.dataset.section;
                // className이 없거나 빈 문자열이면 스킵
                if (!className) continue;
                // 해당 클래스명을 가진 모든 요소 찾기
                const sectionElements = document.getElementsByClassName(className);
            
                // 찾은 모든 요소를 sections 배열에 추가
                // 하나의 클래스명에 여러 요소가 있을 수 있으므로 모두 수집
                for (const element of sectionElements) {
                    sections.push({
                        element: element,        // DOM 요소 (위치 계산용)
                        sectionName: className   // 클래스명 (링크 매칭용)
                    });
                }
            }

            return sections;
        };

        // 클릭 스크롤 상태 추적 변수
        // active 클래스의 "판단 주체"를 스크롤과 클릭으로 분리하기 위함
        let isClickScrolling = false;
        let clickedSection = '';

        /**
         * 스크롤 이벤트 핸들러
         * 스크롤 위치에 따라 네비게이션 표시/숨김 및 활성 섹션 감지
         */
        const handleScroll = () => {
            // 현재 스크롤 위치 및 요소 높이 계산
            const sections = getSections();
            if (sections.length === 0) {
                nav.classList.remove('c-sticky-nav--active');
                rafId = null;
                return;
            }
            const scrollTop = window.pageYOffset;
            const navHeight = nav.offsetHeight;

            // 이번 프레임 기준 방향 계산 (정책 판단 근거)
            const isDown = scrollTop > lastScrollTop;

            // 정책 기반 헤더 높이 즉시 계산 (지연 없음)
            const headerHeight = getPolicyHeaderHeight(isDown);

            // 상태 저장 (UI 클래스 토글용)
            // scrollDirection은 UI 표현용 상태값이며, 정책 판단에는 사용하지 않음
            scrollDirection = isDown ? 'down' : 'up';
            lastScrollTop = scrollTop;

            // 첫 번째 섹션 도달 시점 계산 (네비게이션 표시 기준)
            const threshold = sections[0].element.offsetTop - headerHeight - navHeight - CONFIG.SHOW_NAV_BUFFER;
            nav.classList.toggle('c-sticky-nav--active', scrollTop >= threshold);
            
            // 스크롤 방향에 따른 헤더 표시/숨김 클래스 적용
            nav.classList.toggle('c-sticky-nav--header-hidden', scrollDirection === 'down');
            nav.classList.toggle('c-sticky-nav--header-visible', scrollDirection === 'up');

            // 현재 스크롤 위치에 해당하는 활성 섹션 찾기
            let activeId = '';
            sections.forEach(section => {
                const el = section.element;
                // 섹션의 상단/하단 위치 계산 (헤더 및 네비게이션 높이 고려)
                const top = el.offsetTop - headerHeight - navHeight - CONFIG.ACTIVE_THRESHOLD;
                const bottom = top + el.offsetHeight;
                // 현재 스크롤 위치가 섹션 범위 내에 있으면 활성 섹션으로 설정
                if (scrollTop >= top && scrollTop < bottom) activeId = section.sectionName;
            });

            // 활성 섹션에 해당하는 버튼에 active 클래스 및 aria-current 적용
            // 클릭 스크롤 중이면 스크롤 기반 active 갱신하지 않음 (클릭한 버튼 유지)
            if (!isClickScrolling) {
                links.forEach(link => {
                    const isActive = link.dataset.section === activeId;
                    link.classList.toggle('c-sticky-nav__link--active', isActive);
                    // 접근성: 현재 활성화된 버튼을 스크린 리더에 알림 (같은 페이지 내 섹션 네비게이션)
                    if (isActive) {
                        link.setAttribute('aria-current', 'location');
                    } else {
                        link.removeAttribute('aria-current');
                    }
                });
            }

            // 클릭한 섹션에 도달하면 스크롤 기준으로 복귀
            if (isClickScrolling) {
                const currentSectionReached = activeId === clickedSection;
                if (currentSectionReached) {
                    isClickScrolling = false;
                    clickedSection = '';
                }
            }
            
            // requestAnimationFrame ID 초기화 (다음 스크롤 이벤트를 위해)
            rafId = null;
        };
        
        // requestAnimationFrame ID 추적 (쓰로틀링을 위해)
        let rafId = null;
        
        /**
         * 스크롤 이벤트 래퍼
         * requestAnimationFrame을 활용한 쓰로틀링 적용
         * 이미 실행 예정인 프레임이 있으면 새로 등록하지 않음
         */
        const onScroll = () => {
            // 이미 실행 예정인 프레임이 있으면 스킵 (쓰로틀링)
            if (rafId !== null) return;
            
            // 새로운 프레임 등록
            rafId = requestAnimationFrame(handleScroll);
        };

        // 각 버튼에 클릭 이벤트 등록
        links.forEach(link => {
            link.addEventListener('click', (e) => {
                // data-section 값(클래스명)이 없으면 종료
                const className = link.dataset.section;
                if (!className) return;
                
                // 클릭 스크롤 상태 설정 (스크롤 로직 무시)
                isClickScrolling = true;
                clickedSection = className;

                // 클릭 시 즉시 active 표시 (스크롤 상태 무시)
                links.forEach(l => {
                    const isActive = l.dataset.section === className;
                    l.classList.toggle('c-sticky-nav__link--active', isActive);
                    if (isActive) {
                        l.setAttribute('aria-current', 'location');
                    } else {
                        l.removeAttribute('aria-current');
                    }
                });
                
                // data-section 값(클래스명)으로 타겟 섹션 요소 찾기
                const classElems = document.getElementsByClassName(className);
                if (classElems.length === 0) return;
                
                // 첫 번째 매칭 요소를 타겟으로 설정
                const target = classElems[0];
                
                // 스크롤 방향 확인 (아래로 스크롤할지 위로 스크롤할지)
                const isDown = target.offsetTop > window.pageYOffset;
                
                // 스크롤 방향에 따른 정책 기반 헤더 높이 사용
                const headerHeight = getPolicyHeaderHeight(isDown);
                
                // 스크롤 목표 위치 계산 (헤더 및 네비게이션 높이 고려)
                const scrollTarget = target.offsetTop - headerHeight - nav.offsetHeight;

                // 부드러운 스크롤 이동 (최소값 0 보장)
                window.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
            });
        });

        /**
         * 모바일에서 메뉴 오버플로우 체크
         * 메뉴 너비가 화면 너비를 초과하면 오버플로우 클래스 추가
         */
        const checkOverflow = () => {
            // 데스크톱에서는 오버플로우 체크 불필요
            if (window.innerWidth > CONFIG.MOBILE_BREAKPOINT) {
                list?.classList.remove('c-sticky-nav__list--overflow');
                return;
            }
            
            // 모든 링크의 총 너비 계산 (링크 간격 24px 포함)
            let totalWidth = 0;
            links.forEach((link, i) => {
                totalWidth += link.offsetWidth;
                // 마지막 링크가 아니면 간격 추가
                if (i < links.length - 1) totalWidth += 24;
            });
            
            // 총 너비가 화면 너비를 초과하면 오버플로우 클래스 추가
            list?.classList.toggle('c-sticky-nav__list--overflow', totalWidth > window.innerWidth);
        };

        // 스크롤 이벤트 등록 (requestAnimationFrame 쓰로틀링 적용)
        window.addEventListener('scroll', onScroll, { passive: true });
        // 리사이즈 이벤트 등록 (오버플로우 재체크)
        window.addEventListener('resize', checkOverflow);
        
        // 초기 실행
        checkOverflow();
        handleScroll();
    };

    /**
     * window.stickyNavMenu 설정을 기반으로 네비게이션 동적 생성
     * @returns {HTMLElement|null} 생성된 네비게이션 요소 또는 null
     */
    const createStickyNavFromConfig = () => {
        // window.stickyNavMenu 설정이 없거나 배열이 아니면 종료
        if (!window.stickyNavMenu || !Array.isArray(window.stickyNavMenu)) return null;

        // 헤더 요소가 없으면 종료
        const header = document.querySelector('.c-header');
        if (!header) return null;

        // 이미 sticky nav가 존재하면 중복 생성 방지
        if (document.querySelector('[data-sticky-nav]')) return null;

        // 네비게이션 컨테이너 생성
        const nav = document.createElement('div');
        nav.className = 'c-sticky-nav';
        nav.setAttribute('data-sticky-nav', '');
        nav.setAttribute('role', 'navigation');
        nav.setAttribute('aria-label', 'Page sections navigation');

        // 네비게이션 내부 요소 생성
        const navInner = document.createElement('nav');
        navInner.className = 'c-sticky-nav__nav';
        navInner.setAttribute('aria-label', 'Page sections');

        // 리스트 요소 생성
        const ul = document.createElement('ul');
        ul.className = 'c-sticky-nav__list';

        // window.stickyNavMenu 배열을 순회하며 메뉴 아이템 생성
        window.stickyNavMenu.forEach(menuItem => {
            // 필수 속성 체크: class, label이 모두 있어야 함
            if (!menuItem.class || !menuItem.label) return;
            
            const li = document.createElement('li');
            const button = document.createElement('button');
            
            // 버튼 속성 설정
            button.className = 'c-sticky-nav__link';
            button.setAttribute('data-section', menuItem.class);
            button.setAttribute('aria-label', `Navigate to ${menuItem.label} section`);
            button.textContent = menuItem.label;
            
            // DOM 트리 구성
            li.appendChild(button);
            ul.appendChild(li);
        });

        // DOM 트리 완성
        navInner.appendChild(ul);
        nav.appendChild(navInner);

        // 헤더 다음에 네비게이션 삽입
        header.insertAdjacentElement('afterend', nav);

        return nav;
    };

    /**
     * 초기화 함수
     * 동적 생성 및 기존 네비게이션 초기화 실행
     */
    const syncOfferLink = () => {
        // The offer wrapper is emitted only when its section has visible content.
        // Preserve legacy product-description targets on templates still using them.
        const hasOffer = document.getElementsByClassName('pdp-sticky__01').length > 0;
        document.querySelectorAll('[data-sticky-nav] [data-section="pdp-sticky__01"]').forEach((link) => {
            link.closest('li').hidden = !hasOffer;
        });
    };

    const init = () => {
        // window.stickyNavMenu 설정이 있으면 네비게이션 동적 생성
        createStickyNavFromConfig();
        syncOfferLink();
        // 기존에 존재하는 모든 sticky nav 요소 초기화
        document.querySelectorAll('[data-sticky-nav]').forEach(initStickyNav);
    };

    // DOM이 이미 로드 완료된 경우 즉시 실행
    if (document.readyState === 'complete') init();
    // DOM 로드 완료 대기
    else window.addEventListener('load', init);
    // Shopify 섹션 로드 시 재초기화 (테마 에디터에서 섹션 추가/수정 시)
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', () => requestAnimationFrame(syncOfferLink));
})();
